
import React, { useState, useRef, useMemo } from 'react';
import { useFinance } from '../context/FinanceContext';
import { parseExpenseCSV } from '../services/csvService';
import { Expense, Currency, CategoryBudget } from '../types';
import { 
  Plus, Upload, Trash2, X, Search, Home, Zap, ShoppingCart, Truck, Utensils, Play, HeartPulse, 
  GraduationCap, Plane, Shield, CreditCard, Edit2, BarChart3, PieChart,
  Settings2, TrendingUp, TrendingDown, Target, AlertTriangle, CalendarDays, MousePointer2,
  Sun, Moon, Shuffle, ChevronsUp
} from 'lucide-react';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Bar, Line, ComposedChart, Legend, AreaChart, Area
} from 'recharts';

const CATEGORY_ICONS: Record<string, any> = {
  Housing: Home,
  Utilities: Zap,
  Groceries: ShoppingCart,
  Transportation: Truck,
  Dining: Utensils,
  Entertainment: Play,
  Healthcare: HeartPulse,
  Education: GraduationCap,
  Travel: Plane,
  Insurance: Shield,
  Other: CreditCard
};

const CHART_COLORS = [
  '#1e3a8a', '#1e40af', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#eff6ff'
];

const P_DARK = {
  bg:     '#f8fafc',
  s1:     '#0D1625',
  s2:     '#111E33',
  s3:     '#162340',
  border: '#1A2940',
  muted:  '#475569',
  subtle: '#94a3b8',
  text:   '#F8FAFC',
  gold:   '#C9A84C',
  goldLt: '#E8C96A',
  teal:   '#2DD4BF',
  tealDk: '#0F8A7C',
  rose:   '#F87171',
  sky:    '#38BDF8',
  violet: '#A78BFA',
  amber:  '#FBBF24',
  green:  '#34D399',
  c1:'#3B82F6', c2:'#2DD4BF', c3:'#C9A84C', c4:'#A78BFA', c5:'#F87171', c6:'#34D399', c7:'#FB923C',
};

const P_LIGHT = {
  bg:     '#f8fafc',
  s1:     '#ffffff',
  s2:     '#ffffff',
  s3:     '#f1f5f9',
  border: '#e2e8f0',
  muted:  '#64748b',
  subtle: '#94a3b8',
  text:   '#0f172a',
  gold:   '#C9A84C',
  goldLt: '#E8C96A',
  teal:   '#0d9488',
  tealDk: '#0f766e',
  rose:   '#e11d48',
  sky:    '#0284c7',
  violet: '#7c3aed',
  amber:  '#d97706',
  green:  '#16a34a',
  c1:'#2563eb', c2:'#0d9488', c3:'#b45309', c4:'#7c3aed', c5:'#e11d48', c6:'#16a34a', c7:'#ea580c',
};

const getP = (isDarkMode: boolean) => isDarkMode ? P_DARK : P_LIGHT;
const fmt = (n: number, d = 0) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

const DarkTip = ({ active, payload, label }: any) => {
  const { isDarkMode, currency } = useFinance();
  const P = getP(isDarkMode);
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: P.s3, border: `1px solid ${P.border}`, borderRadius: 10, padding: '9px 13px', fontSize: 12, color: P.text }}>
      {label && <div style={{ color: P.subtle, marginBottom: 5, fontSize: 11 }}>{label}</div>}
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ width: 7, height: 7, borderRadius: 2, background: p.fill || p.stroke || p.color, display: 'inline-block' }} />
          <span style={{ color: P.subtle }}>{p.name}:</span>
          <span style={{ fontWeight: 655 }}>{currency} {typeof p.value === 'number' ? fmt(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June', 
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Expenses: React.FC = () => {
  const { 
    expenses, budgets, addExpenses, deleteExpense, updateExpense, 
    currency, updateBudget, addCategory, convertToDisplay, convertFromInput, exchangeRates,
    isDarkMode, setIsDarkMode,
    selectedYear, setSelectedYear, availableYears
  } = useFinance();

  const P = getP(isDarkMode);
  const CC = [P.c1, P.c2, P.c3, P.c4, P.c5, P.c6, P.c7];

  const [searchTerm, setSearchTerm] = useState('');
  const yearsRange = availableYears;
  const performanceYear = selectedYear;
  const setPerformanceYear = (yr: number) => setSelectedYear(yr);
  // selectedMonths is an array of indices. Empty means "Full Year / All Months".
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);

  const yearString = performanceYear.toString();

  const filteredExpenses = useMemo(() => {
    const isPeriodSelected = selectedMonths.length > 0;
    const monthStrings = selectedMonths.map(m => (m + 1).toString().padStart(2, '0'));
    return expenses.filter(e => {
      const isWithinYear = e.date.startsWith(yearString);
      const isWithinMonth = !isPeriodSelected || monthStrings.includes(e.date.split('-')[1]);
      const matchesSearch = 
        e.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.amount.toString().includes(searchTerm);
      return isWithinYear && isWithinMonth && matchesSearch;
    }).sort((a, b) => b.date.localeCompare(a.date));
  }, [expenses, yearString, selectedMonths, searchTerm]);

  const monthlyExpenseTrend = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const mStr = `${performanceYear}-${(i + 1).toString().padStart(2, '0')}`;
      const d = new Date(performanceYear, i, 1);
      const label = d.toLocaleString('default', { month: 'short' });
      // Calculate total monthly budget
      const budgetTotal = budgets.reduce((s, b) => s + b.budget, 0);
      const point: any = { label, budget: convertToDisplay(budgetTotal) };
      budgets.forEach(b => {
        const spentInCat = expenses
          .filter(e => e.category === b.category && e.date.startsWith(mStr))
          .reduce((s, e) => s + e.amount, 0);
        point[b.category] = convertToDisplay(spentInCat);
      });
      return point;
    });
  }, [expenses, budgets, convertToDisplay, performanceYear]);

  const monthlyTotals = useMemo(() => {
    return monthlyExpenseTrend.map(point => {
      let total = 0;
      budgets.forEach(b => {
        total += point[b.category] || 0;
      });
      return {
        label: point.label,
        total,
        budget: point.budget,
      };
    });
  }, [monthlyExpenseTrend, budgets]);

  const expenseInsights = useMemo(() => {
    const totals = monthlyTotals.map(t => t.total);
    const validMonths = totals.filter(t => t > 0);
    
    if (validMonths.length === 0) {
      return {
        variability: "No spending recorded yet.",
        variabilityTitle: "N/A",
        variabilityPercent: 0,
        varLevel: "low",
        overspend: "No overspent categories or months detected for this year.",
        overspendLevel: "low",
        suggestedAction: "Establish a baseline of expenses to generate personalized optimization steps.",
        stats: { avg: 0, peakMonth: 'N/A', peakVal: 0, lowMonth: 'N/A', lowVal: 0, varCoeff: 0, overLimitMonths: 0 }
      };
    }
    
    // Mean of months with spend
    const sum = totals.reduce((s, x) => s + x, 0);
    const activeMonthsCount = validMonths.length;
    const avgSpent = sum / activeMonthsCount;
    
    // Find peak and lowest months among those with spend
    let peakVal = -1;
    let peakMonth = 'N/A';
    let lowVal = Infinity;
    let lowMonth = 'N/A';
    
    monthlyTotals.forEach(m => {
      if (m.total > 0) {
        if (m.total > peakVal) {
          peakVal = m.total;
          peakMonth = m.label;
        }
        if (m.total < lowVal) {
          lowVal = m.total;
          lowMonth = m.label;
        }
      }
    });

    if (lowVal === Infinity) {
      lowVal = 0;
      lowMonth = 'N/A';
    }
    
    // Standard deviation and Coefficient of variation
    const sqDiffs = validMonths.map(t => Math.pow(t - avgSpent, 2));
    const avgSqDiff = sqDiffs.reduce((s, x) => s + x, 0) / activeMonthsCount;
    const stdDev = Math.sqrt(avgSqDiff);
    const varCoeff = avgSpent > 0 ? (stdDev / avgSpent) * 100 : 0;
    
    let variabilityTitle = "Stable Spending";
    let variabilityDesc = "";
    let varLevel: 'low' | 'medium' | 'high' = 'low';
    
    if (varCoeff > 25) {
      varLevel = 'high';
      variabilityTitle = "High Fluctuations";
      variabilityDesc = `Highly volatile (±${varCoeff.toFixed(1)}%). Large swing between peak ${peakMonth} (${currency} ${fmt(peakVal)}) and lowest month ${lowMonth} (${currency} ${fmt(lowVal)}).`;
    } else if (varCoeff > 10) {
      varLevel = 'medium';
      variabilityTitle = "Moderate Volatility";
      variabilityDesc = `Moderately stable (±${varCoeff.toFixed(1)}%). Minor variance with occasional discretionary peaks in ${peakMonth}.`;
    } else {
      varLevel = 'low';
      variabilityTitle = "Consistently Fixed";
      variabilityDesc = `Extremely stable baseline (±${varCoeff.toFixed(1)}%). Core costs are predictable with minimal variance month-to-month.`;
    }
    
    // Monthly budget limit checks
    let overLimitMonths = 0;
    let totalOverspentAmount = 0;
    monthlyTotals.forEach(m => {
      if (m.total > m.budget) {
        overLimitMonths++;
        totalOverspentAmount += (m.total - m.budget);
      }
    });
    
    // Worst budget leak category
    const categoryTotals: Record<string, { spent: number, budget: number }> = {};
    budgets.forEach(b => {
      categoryTotals[b.category] = { spent: 0, budget: b.budget * 12 };
    });
    
    filteredExpenses.forEach(e => {
      if (categoryTotals[e.category]) {
        categoryTotals[e.category].spent += e.amount;
      } else {
        categoryTotals[e.category] = { spent: e.amount, budget: 0 };
      }
    });
    
    let worstCat = '';
    let worstOverspendAmount = 0;
    
    Object.entries(categoryTotals).forEach(([cat, data]) => {
      const budgetInDisplay = convertToDisplay(data.budget);
      const spentInDisplay = convertToDisplay(data.spent);
      const over = spentInDisplay - budgetInDisplay;
      if (over > worstOverspendAmount) {
        worstOverspendAmount = over;
        worstCat = cat;
      }
    });
    
    let overspendDesc = "";
    let overspendLevel: 'low' | 'medium' | 'high' = 'low';
    
    if (overLimitMonths > 0) {
      if (overLimitMonths >= 6) {
        overspendLevel = 'high';
        overspendDesc = `Breaches in ${overLimitMonths} of 12 months. Net budget leakage is ${currency} ${fmt(totalOverspentAmount)}.`;
      } else {
        overspendLevel = 'medium';
        overspendDesc = `${overLimitMonths} overspent months. Total budget leakage is ${currency} ${fmt(totalOverspentAmount)}.`;
      }
      
      if (worstCat) {
        overspendDesc += ` Key vector: "${worstCat}" (+${currency} ${fmt(worstOverspendAmount)} of cumulative margin).`;
      }
    } else {
      overspendDesc = `Flawless execution. No monthly cap exceeded across all current cycles for ${performanceYear}.`;
    }
    
    // Suggested actions
    let suggestedAction = "";
    if (overspendLevel === 'high') {
      suggestedAction = `Implement zero-based tracking. Restructure or downscale "${worstCat || 'discretionary'}" category immediately, and apply mid-month alert boundaries.`;
    } else if (varLevel === 'high') {
      suggestedAction = `Create an irregular expense sinking fund of ${currency} ${fmt(stdDev * 1.5, 0)} to absorb seasonal and annual billing spikes.`;
    } else if (overLimitMonths > 0) {
      suggestedAction = `Reallocate category balances since "${worstCat}" regularly drains quotas. Fund it by moving unused surplus from stable buckets.`;
    } else {
      suggestedAction = `Superb optimization. Consider reallocating standard liquid savings to high-yield accounts or increasing your automated investment drafts.`;
    }
    
    return {
      variability: variabilityDesc,
      variabilityTitle,
      variabilityPercent: varCoeff,
      varLevel,
      overspend: overspendDesc,
      overspendLevel,
      suggestedAction,
      stats: {
        avg: avgSpent,
        peakMonth,
        peakVal,
        lowMonth,
        lowVal,
        varCoeff,
        overLimitMonths
      }
    };
  }, [monthlyTotals, budgets, filteredExpenses, currency, performanceYear, convertToDisplay]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [expenseModal, setExpenseModal] = useState<{ isOpen: boolean, mode: 'add' | 'edit', data: any }>({
    isOpen: false,
    mode: 'add',
    data: { id: '', date: new Date().toISOString().split('T')[0], description: '', amount: '', category: 'Other', currency: currency }
  });
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', budget: '', icon: 'Other' });
  const [editingCategory, setEditingCategory] = useState<{ originalName: string, name: string, budget: string, icon: string } | null>(null);

  // Toggle month selection logic
  const toggleMonth = (idx: number) => {
    setSelectedMonths(prev => 
      prev.includes(idx) ? prev.filter(m => m !== idx) : [...prev, idx]
    );
  };

  const kpis = useMemo(() => {
    const totalSpent = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
    const monthlyTotalBudget = budgets.reduce((sum, b) => sum + b.budget, 0);
    const yearlyBudget = monthlyTotalBudget * 12;
    const balance = yearlyBudget - totalSpent;
    const deviation = yearlyBudget > 0 ? (balance / yearlyBudget) * 100 : 0;

    // Average monthly calculated as Yearly Total / 12
    const avgMonthlySpent = totalSpent / 12;

    return {
      totalSpent: convertToDisplay(totalSpent),
      yearlyBudget: convertToDisplay(yearlyBudget),
      avgMonthlySpent: convertToDisplay(avgMonthlySpent),
      avgMonthlyBudget: convertToDisplay(monthlyTotalBudget),
      balance: convertToDisplay(balance),
      deviation
    };
  }, [filteredExpenses, budgets, convertToDisplay]);

  const activeCategories = useMemo(() => {
    const fromBudgets = budgets.map(b => b.category);
    const fromExpenses = Array.from(new Set(filteredExpenses.map(e => e.category)));
    return Array.from(new Set([...fromBudgets, ...fromExpenses])).sort();
  }, [budgets, filteredExpenses]);

  const chartData = useMemo(() => {
    const data: any[] = [];
    for (let m = 0; m < 12; m++) {
      const label = new Date(performanceYear, m, 1).toLocaleString('default', { month: 'short' });
      const monthStr = `${yearString}-${(m + 1).toString().padStart(2, '0')}`;
      
      const point: any = { 
        name: label, 
        budget: convertToDisplay(budgets.reduce((s, b) => s + b.budget, 0)) 
      };
      
      activeCategories.forEach(catName => {
        const spentInCat = expenses
          .filter(e => e.category === catName && e.date.startsWith(monthStr))
          .reduce((s, e) => s + e.amount, 0);
        point[catName] = convertToDisplay(spentInCat);
      });

      data.push(point);
    }
    return data;
  }, [performanceYear, yearString, activeCategories, budgets, expenses, convertToDisplay]);

  // Unified Performance Data: Dynamically switches between Monthly, Multi-month, or Annual
  const periodPerformanceData = useMemo(() => {
    const isPeriodSelected = selectedMonths.length > 0;
    const monthStrings = selectedMonths.map(m => (m + 1).toString().padStart(2, '0'));
    
    return budgets.map(b => {
      let spentInPeriod = 0;
      if (isPeriodSelected) {
        // Sum expenses for specific months
        spentInPeriod = expenses
          .filter(e => {
            const mPart = e.date.split('-')[1];
            return e.category === b.category && e.date.startsWith(yearString) && monthStrings.includes(mPart);
          })
          .reduce((sum, e) => sum + e.amount, 0);
      } else {
        // Default: Full Year
        spentInPeriod = expenses
          .filter(e => e.category === b.category && e.date.startsWith(yearString))
          .reduce((sum, e) => sum + e.amount, 0);
      }
      
      const multiplier = isPeriodSelected ? selectedMonths.length : 12;
      const periodBudget = b.budget * multiplier;
      const consumption = periodBudget > 0 ? (spentInPeriod / periodBudget) * 100 : 0;
      const gap = periodBudget - spentInPeriod;

      return {
        ...b,
        spent: spentInPeriod,
        periodBudget,
        consumption,
        gap
      };
    }).sort((a, b) => b.spent - a.spent);
  }, [budgets, expenses, yearString, selectedMonths]);

  const handleExpenseSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const inputAmt = parseFloat(expenseModal.data.amount);
    if (isNaN(inputAmt)) return;

    const rate = exchangeRates[expenseModal.data.currency as Currency] || 1;
    const amountInBase = inputAmt / rate;

    const entry: Expense = {
      id: expenseModal.mode === 'add' ? Date.now().toString() : expenseModal.data.id,
      date: expenseModal.data.date,
      description: expenseModal.data.description,
      amount: amountInBase,
      category: expenseModal.data.category,
      currency: expenseModal.data.currency
    };

    if (expenseModal.mode === 'add') {
      addExpenses([entry]);
    } else {
      updateExpense(entry);
    }
    setExpenseModal({ ...expenseModal, isOpen: false });
  };

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = await parseExpenseCSV(text);
      if (parsed.length > 0) {
        addExpenses(parsed);
      }
    } catch (err) {
      alert("CSV Import Failed.");
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEditCategory = (b: CategoryBudget) => {
    setEditingCategory({
      originalName: b.category,
      name: b.category,
      budget: convertToDisplay(b.budget).toString(),
      icon: b.icon || 'Other'
    });
  };

  const handleSaveCategoryEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory) return;
    updateBudget(
      editingCategory.name, 
      convertFromInput(parseFloat(editingCategory.budget)), 
      editingCategory.icon, 
      editingCategory.originalName
    );
    setEditingCategory(null);
  };

  return (
    <div className="space-y-8 sm:space-y-10 pb-12 sm:pb-20 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 sm:gap-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter leading-none">Expenses</h1>
          <p className="text-slate-400 font-bold text-xs sm:text-sm uppercase tracking-widest mt-2">Multi-period financial audit</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
          <div className="flex gap-2">
            <button 
              onClick={() => setIsCategoryModalOpen(true)}
              className="flex-1 sm:flex-none bg-white border-2 border-slate-900 text-slate-900 px-4 sm:px-6 py-3.5 rounded-2xl text-[11px] font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition-all"
            >
              <PieChart size={18}/> Categories
            </button>
            <button 
              onClick={() => {
                const now = new Date();
                const defaultDate = selectedYear === now.getFullYear()
                  ? now.toISOString().split('T')[0]
                  : `${selectedYear}-01-15`;
                setExpenseModal({ isOpen: true, mode: 'add', data: { date: defaultDate, description: '', amount: '', category: 'Other', currency: currency } });
              }}
              className="flex-1 sm:flex-none bg-slate-900 text-white px-4 sm:px-8 py-3.5 rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-slate-200 flex items-center justify-center gap-2 active:scale-95 transition-all"
            >
              <Plus size={20}/> New Entry
            </button>
          </div>
        </div>
      </header>

      {/* Month Selector with Toggle Capability */}
      <div className="bg-white p-3 sm:p-4 rounded-[32px] border border-slate-100 shadow-sm flex flex-col gap-4 overflow-hidden">
        <div className="flex items-center justify-between px-3 pt-1">
           <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
             <MousePointer2 size={14}/> Targeted Audit (Swipe)
           </p>
           {selectedMonths.length > 0 && (
             <button 
               onClick={() => setSelectedMonths([])}
               className="text-[11px] font-black text-blue-600 hover:text-slate-900 transition-colors uppercase tracking-widest px-2 py-1"
             >
               Clear
             </button>
           )}
        </div>
        <div className="overflow-x-auto no-scrollbar flex gap-2 pb-1 snap-x">
          {MONTHS.map((month, idx) => (
            <button
              key={month}
              onClick={() => toggleMonth(idx)}
              className={`snap-start min-w-[120px] px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${selectedMonths.includes(idx) ? 'bg-blue-600 text-white shadow-lg shadow-blue-100 scale-[1.02]' : 'text-slate-400 bg-slate-50/50 hover:bg-slate-100 hover:text-slate-900'}`}
            >
              {month}
            </button>
          ))}
        </div>
      </div>

      {/* Year Totals KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white p-6 sm:p-8 rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm flex flex-col justify-between group hover:border-blue-200 transition-colors">
           <div>
             <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 leading-none">Total Spent ({performanceYear})</p>
             <h3 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter">
               {currency} {kpis.totalSpent.toLocaleString(undefined, { maximumFractionDigits: 0 })}
             </h3>
           </div>
           <div className="mt-6 pt-6 border-t border-slate-50">
             <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tight">
               Avg Monthly: <span className="text-slate-900 font-black">{currency} {kpis.avgMonthlySpent.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
             </p>
           </div>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm flex flex-col justify-between group hover:border-blue-200 transition-colors">
           <div>
             <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 leading-none">Target Budget ({performanceYear})</p>
             <h3 className="text-3xl sm:text-4xl font-black text-slate-700 tracking-tighter">
               {currency} {kpis.yearlyBudget.toLocaleString(undefined, { maximumFractionDigits: 0 })}
             </h3>
           </div>
           <div className="mt-6 pt-6 border-t border-slate-50">
             <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tight">
               Allowance: <span className="text-blue-600 font-black">{currency} {kpis.avgMonthlyBudget.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
             </p>
           </div>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm group hover:shadow-md transition-all flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-start mb-4">
              <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-none">Balance</p>
              <div className={`flex items-center gap-1 text-[12px] font-black ${kpis.deviation >= 0 ? 'text-teal-500' : 'text-rose-500'}`}>
                {kpis.deviation >= 0 ? <TrendingDown size={14}/> : <TrendingUp size={14}/>}
                {Math.abs(kpis.deviation).toFixed(1)}%
              </div>
            </div>
            <h3 className={`text-3xl sm:text-4xl font-black tracking-tighter ${kpis.balance >= 0 ? 'text-teal-500' : 'text-rose-500'}`}>
              {currency} {Math.abs(kpis.balance).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </h3>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-50">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tight">
              Status: <span className={`font-black uppercase ${kpis.balance >= 0 ? 'text-teal-500' : 'text-rose-500'}`}>{kpis.deviation >= 0 ? 'Surplus' : 'Deficit'}</span>
            </p>
          </div>
        </div>
      </div>

      {/* 12-Month Expense Trend Graph with Key Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        {/* Main Expense Trend Graph */}
        <div className="lg:col-span-2 bg-white rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm p-6 sm:p-10 overflow-hidden flex flex-col justify-between">
          <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 sm:mb-10 gap-4">
              <div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                  <BarChart3 size={24} className="text-blue-600"/> 12-Month Expense Trend
                </h3>
                <p className="text-sm text-slate-400 font-medium leading-relaxed">Stacked dynamic trends for the calendar year with budget limit line</p>
              </div>
            </div>
            <div className="h-64 sm:h-96 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyExpenseTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    {budgets.slice(0, 10).map((b, i) => (
                      <linearGradient key={b.category} id={`g${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CC[i % CC.length]} stopOpacity={0.7} />
                        <stop offset="100%" stopColor={CC[i % CC.length]} stopOpacity={0.15} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={P.border} />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fontWeight: 700, fill: P.subtle }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: P.subtle }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<DarkTip />} cursor={{ stroke: P.border, strokeWidth: 1 }} />
                  <Legend verticalAlign="top" align="center" iconType="circle" wrapperStyle={{paddingBottom: '20px', fontSize: '11px', fontWeight: 'bold', color: P.text}} />
                  {budgets.slice(0, 10).map((b, i) => (
                    <Area key={b.category} type="monotone" dataKey={b.category} stackId="1" stroke={CC[i % CC.length]} strokeWidth={1.5} fill={`url(#g${i % CC.length})`} />
                  ))}
                  <Line type="monotone" dataKey="budget" name="Budget Cap" stroke={P.gold} strokeWidth={2.5} strokeDasharray="5 4" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Expense Insights Card */}
        <div className="lg:col-span-1 bg-white rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm p-6 sm:p-10 flex flex-col justify-between">
          <div>
            <div className="mb-6">
              <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                <Zap size={22} className="text-amber-500 fill-amber-300 shrink-0" /> Expense Insights
              </h3>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Full-year audit observations</p>
            </div>

            <div className="space-y-4">
              {/* Variability Section */}
              <div className="p-4 bg-slate-50 border border-slate-100/60 rounded-2xl text-left">
                <div className="flex items-start gap-3">
                  <span className="p-2.5 bg-blue-50/80 text-blue-600 rounded-xl mt-0.5 shrink-0">
                    <Shuffle size={16} />
                  </span>
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5 leading-none">
                      Variability <span className={`normal-case font-black ${expenseInsights.varLevel === 'high' ? 'text-rose-500' : expenseInsights.varLevel === 'medium' ? 'text-amber-500' : 'text-teal-500'}`}>({expenseInsights.variabilityPercent.toFixed(0)}%)</span>
                    </h4>
                    <p className="text-sm font-black text-slate-900 mt-1.5 leading-tight">{expenseInsights.variabilityTitle}</p>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed font-semibold">{expenseInsights.variability}</p>
                  </div>
                </div>
              </div>

              {/* Overspend Section */}
              <div className="p-4 bg-slate-50 border border-slate-100/60 rounded-2xl text-left">
                <div className="flex items-start gap-3">
                  <span className="p-2.5 bg-rose-50/80 text-rose-600 rounded-xl mt-0.5 shrink-0">
                    <AlertTriangle size={16} />
                  </span>
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 leading-none">Overspend Index</h4>
                    <p className="text-sm font-black text-slate-900 mt-1.5 leading-tight">
                      {expenseInsights.stats.overLimitMonths > 0 ? `${expenseInsights.stats.overLimitMonths} Cap Breaches` : 'Within Boundaries'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed font-semibold">{expenseInsights.overspend}</p>
                  </div>
                </div>
              </div>

              {/* Suggested Action Section */}
              <div className="p-4 bg-indigo-50/40 border border-indigo-100/40 rounded-2xl text-left">
                <div className="flex items-start gap-3">
                  <span className="p-2.5 bg-indigo-100/60 text-indigo-600 rounded-xl mt-0.5 shrink-0">
                    <ChevronsUp size={16} />
                  </span>
                  <div>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-600 leading-none">Suggested Action</h4>
                    <p className="text-xs text-indigo-900 mt-2.5 leading-relaxed font-bold">{expenseInsights.suggestedAction}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Period Categorical Audit */}
      <div className="bg-white rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm p-6 sm:p-10">
        <div className="mb-8 sm:mb-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3">
              <CalendarDays size={24} className="text-blue-600"/> 
              {selectedMonths.length === 0 ? `Full Year Analysis` : `Period Performance`}
            </h3>
            <p className="text-sm text-slate-400 font-medium leading-relaxed">
              {selectedMonths.length === 0 
                ? 'Comprehensive full year consumption' 
                : `${selectedMonths.length} month cumulative breakdown`}
            </p>
          </div>
          <div className="px-4 py-2 bg-slate-50 rounded-xl text-[11px] font-black text-slate-400 uppercase tracking-widest border border-slate-100">
             {selectedMonths.length === 0 ? 'Full Year' : `${selectedMonths.length} Months`}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
          {periodPerformanceData.map((b) => {
            const Icon = CATEGORY_ICONS[b.icon || 'Other'] || CreditCard;
            const isOver = b.gap < 0;
            const gapValue = convertToDisplay(Math.abs(b.gap));
            return (
              <div key={b.id || b.category} className="p-6 sm:p-8 rounded-[32px] bg-slate-50/30 border border-slate-100 hover:border-blue-200 transition-all group/item">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3.5 sm:p-4 bg-white rounded-2xl text-slate-400 group-hover/item:text-blue-600 shadow-sm transition-colors">
                      <Icon size={24}/>
                    </div>
                    <div>
                      <p className="text-base sm:text-lg font-black text-slate-900 leading-tight">{b.category}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Budget: {currency}{convertToDisplay(b.periodBudget).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-[10px] font-black uppercase tracking-tight flex items-center gap-1 justify-end ${isOver ? 'text-rose-500' : 'text-teal-500'}`}>
                      {isOver ? 'Overspend' : 'Remaining'}
                    </p>
                    <p className={`text-xl sm:text-2xl font-black tracking-tighter ${isOver ? 'text-rose-500' : 'text-teal-500'}`}>
                      {currency} {gapValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-end mb-1">
                    <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Load ({b.consumption.toFixed(0)}%)</p>
                    <p className="text-[12px] font-black text-slate-900">{currency}{convertToDisplay(b.spent).toLocaleString()} used</p>
                  </div>
                  <div className="relative h-3 w-full bg-white rounded-full border border-slate-100 overflow-hidden shadow-inner">
                    <div 
                      className={`absolute left-0 top-0 h-full transition-all duration-1000 ${isOver ? 'bg-rose-500' : 'bg-blue-600'}`} 
                      style={{ width: `${Math.min(b.consumption, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {periodPerformanceData.length === 0 && (
            <p className="col-span-full text-center py-20 text-slate-300 font-black uppercase text-xs tracking-widest">No budgeted categories defined</p>
          )}
        </div>
      </div>

      {/* Ledger Section with Improved Table Responsibility */}
      <div className="bg-white rounded-[32px] sm:rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 sm:p-10 border-b border-slate-50 flex flex-col md:flex-row justify-between items-start sm:items-center gap-6">
          <div>
            <h3 className="text-xl font-black text-slate-900 tracking-tight leading-none">Transactions ({performanceYear})</h3>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-2">Full year activity log</p>
          </div>
          <div className="flex w-full md:w-auto gap-4">
             <div className="relative flex-1 md:w-64">
               <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18}/>
               <input 
                 placeholder="Search log..." 
                 value={searchTerm} 
                 onChange={e => setSearchTerm(e.target.value)} 
                 className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-none rounded-2xl text-sm font-bold outline-none ring-2 ring-transparent focus:ring-blue-600 transition-all placeholder:text-slate-300"
               />
             </div>
             <button 
               onClick={() => fileInputRef.current?.click()}
               className="p-4 bg-slate-50 text-slate-400 rounded-2xl hover:text-blue-600 hover:bg-slate-100 transition-all shadow-sm active:bg-slate-200"
             >
               <Upload size={20}/>
             </button>
             <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={handleCsvImport}/>
          </div>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left min-w-[600px]">
            <thead>
              <tr className="text-[11px] font-black text-slate-400 uppercase tracking-widest border-b">
                <th className="px-6 sm:px-10 py-6">Timestamp</th>
                <th className="px-6 sm:px-10 py-6">Description</th>
                <th className="px-6 sm:px-10 py-6">Category</th>
                <th className="px-6 sm:px-10 py-6 text-right">Value</th>
                <th className="px-6 sm:px-10 py-6 text-right">Control</th>
              </tr>
            </thead>
            <tbody className="divide-y text-sm font-bold text-slate-700">
              {filteredExpenses.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-6 sm:px-10 py-6 text-slate-400 font-medium">{e.date}</td>
                  <td className="px-6 sm:px-10 py-6 font-black text-slate-900 leading-snug">{e.description}</td>
                  <td className="px-6 sm:px-10 py-6">
                    <span className="px-3 py-1.5 bg-slate-50 rounded-xl text-[10px] font-black uppercase text-slate-400 border border-slate-100">
                      {e.category}
                    </span>
                  </td>
                  <td className="px-6 sm:px-10 py-6 text-right font-black text-slate-900">
                    {currency} {convertToDisplay(e.amount).toLocaleString()}
                  </td>
                  <td className="px-6 sm:px-10 py-6 text-right">
                    <div className="flex justify-end gap-1 sm:opacity-0 group-hover:opacity-100 transition-all">
                      <button 
                        onClick={() => setExpenseModal({ 
                          isOpen: true, 
                          mode: 'edit', 
                          data: { ...e, amount: convertToDisplay(e.amount).toFixed(2), currency: e.currency || currency } 
                        })}
                        className="p-3 text-slate-300 hover:text-blue-600 active:scale-90"
                      >
                        <Edit2 size={18}/>
                      </button>
                      <button 
                        onClick={() => deleteExpense(e.id)}
                        className="p-3 text-slate-300 hover:text-rose-500 active:scale-90"
                      >
                        <Trash2 size={18}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredExpenses.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-10 py-20 text-center text-slate-300 font-black uppercase tracking-widest text-sm">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expense Modal with better mobile heights */}
      {expenseModal.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-[32px] sm:rounded-[40px] max-w-lg w-full p-8 sm:p-12 shadow-2xl relative border border-slate-50 max-h-[95vh] overflow-y-auto custom-scrollbar">
            <button onClick={() => setExpenseModal({ ...expenseModal, isOpen: false })} className="absolute top-6 right-6 text-slate-300 p-2 hover:text-slate-900 transition-all"><X size={28}/></button>
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter mb-8">{expenseModal.mode === 'add' ? 'Log Expense' : 'Modify Record'}</h2>
            <form onSubmit={handleExpenseSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Date</label>
                  <input type="date" required value={expenseModal.data.date} onChange={e=>setExpenseModal({...expenseModal, data:{...expenseModal.data, date:e.target.value}})} className="w-full p-4.5 bg-slate-50 rounded-2xl text-base font-bold outline-none ring-2 ring-transparent focus:ring-blue-500 transition-all border border-slate-100" />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Bucket</label>
                  <select required value={expenseModal.data.category} onChange={e=>setExpenseModal({...expenseModal, data:{...expenseModal.data, category:e.target.value}})} className="w-full p-4.5 bg-slate-50 rounded-2xl text-base font-bold outline-none border border-slate-100 min-h-[56px] appearance-none">
                    {budgets.map(b => <option key={b.id || b.category} value={b.category}>{b.category}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Description</label>
                <input placeholder="What was this for?" required value={expenseModal.data.description} onChange={e=>setExpenseModal({...expenseModal, data:{...expenseModal.data, description:e.target.value}})} className="w-full p-4.5 bg-slate-50 rounded-2xl text-base font-bold outline-none ring-2 ring-transparent focus:ring-blue-500 transition-all border border-slate-100" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Currency</label>
                  <select value={expenseModal.data.currency} onChange={e=>setExpenseModal({...expenseModal, data:{...expenseModal.data, currency:e.target.value}})} className="w-full p-4.5 bg-slate-50 rounded-2xl text-base font-bold outline-none border border-slate-100 min-h-[56px] appearance-none">
                    {Object.values(Currency).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Amount</label>
                  <input type="number" step="0.01" required value={expenseModal.data.amount} onChange={e=>setExpenseModal({...expenseModal, data:{...expenseModal.data, amount:e.target.value}})} className="w-full p-4.5 bg-slate-50 rounded-2xl text-base font-black outline-none ring-2 ring-transparent focus:ring-blue-600 transition-all border border-slate-100" />
                </div>
              </div>
              <button type="submit" className="w-full bg-slate-900 text-white font-black py-5 rounded-3xl shadow-xl hover:bg-blue-600 active:scale-[0.98] transition-all uppercase tracking-widest text-sm mt-4">
                {expenseModal.mode === 'add' ? 'Establish Entry' : 'Update Record'}
              </button>
            </form>
          </div>
        </div>
      )}

      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 animate-in fade-in">
          <div className="bg-white rounded-[32px] sm:rounded-[40px] max-w-2xl w-full p-8 sm:p-12 shadow-2xl relative border border-slate-50 max-h-[95vh] overflow-y-auto custom-scrollbar">
            <button onClick={() => setIsCategoryModalOpen(false)} className="absolute top-6 right-6 text-slate-300 p-2 hover:text-slate-900 transition-all"><X size={28}/></button>
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter mb-8">Management</h2>
            
            <div className="bg-slate-50/50 p-6 sm:p-8 rounded-[32px] border border-slate-100 mb-8">
              <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-6 leading-none">
                {editingCategory ? `Edit: ${editingCategory.originalName}` : 'New Category Target'}
              </h3>
              <form onSubmit={editingCategory ? handleSaveCategoryEdit : (e) => {
                e.preventDefault();
                addCategory(newCategory.name, convertFromInput(parseFloat(newCategory.budget)), newCategory.icon);
                setNewCategory({ name: '', budget: '', icon: 'Other' });
              }} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input placeholder="Category Name" required value={editingCategory ? editingCategory.name : newCategory.name} onChange={e=> editingCategory ? setEditingCategory({...editingCategory, name:e.target.value}) : setNewCategory({...newCategory, name:e.target.value})} className="w-full p-4.5 bg-white border border-slate-100 rounded-2xl text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="number" step="0.01" placeholder="Monthly Limit" required value={editingCategory ? editingCategory.budget : newCategory.budget} onChange={e=> editingCategory ? setEditingCategory({...editingCategory, budget:e.target.value}) : setNewCategory({...newCategory, budget:e.target.value})} className="w-full p-4.5 bg-white border border-slate-100 rounded-2xl text-sm font-black outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex gap-3">
                  {editingCategory && (
                    <button type="button" onClick={() => setEditingCategory(null)} className="flex-1 bg-slate-100 text-slate-500 font-black py-4.5 rounded-2xl uppercase tracking-widest text-[11px] active:scale-95">Cancel</button>
                  )}
                  <button type="submit" className="flex-[2] bg-slate-900 text-white font-black py-4.5 rounded-2xl shadow-md uppercase tracking-widest text-[11px] active:scale-95">
                    {editingCategory ? 'Commit Update' : 'Initialize Category'}
                  </button>
                </div>
              </form>
            </div>

            <div className="space-y-4 px-1 pb-4">
              {budgets.map(b => (
                <div key={b.id || b.category} className="flex justify-between items-center p-5 bg-slate-50 rounded-[28px] group border border-transparent hover:border-slate-200 transition-all shadow-sm">
                   <div className="flex items-center gap-4">
                      <div className="p-3 bg-white rounded-2xl text-slate-400 shadow-sm">
                        {React.createElement(CATEGORY_ICONS[b.icon || 'Other'] || CreditCard, { size: 20 })}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-black text-slate-900 text-sm leading-tight">{b.category}</span>
                        <p className="text-[12px] font-black text-blue-600 mt-1">{currency} {convertToDisplay(b.budget).toLocaleString()}</p>
                      </div>
                   </div>
                   <div className="flex items-center gap-1">
                      <button onClick={() => handleEditCategory(b)} className="p-3 text-slate-300 hover:text-blue-600 active:scale-90 transition-all"><Settings2 size={20}/></button>
                      <button onClick={() => { if(window.confirm('Delete category?')) addCategory(b.category, 0, b.icon); }} className="p-3 text-slate-300 hover:text-rose-500 active:scale-90 transition-all"><Trash2 size={20}/></button>
                   </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
